import {useTranslation} from "react-i18next";
import CommonClass from "@/utils/common";
import clsx from "clsx";
import st from "@/styles/CreditLineListItem.module.scss";
import Button from "@/ui/Button.tsx";
import DashboardClass from "@/utils/dashboard_class";
import {useNavigate} from 'react-router-dom';
import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import useCommon from "@/hooks/common.ts";
import React, {useEffect, useRef, useState} from "react";
import {useAtom} from "jotai/index";
import {profile} from "@/atoms/profile.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import {CircularProgress} from "@mui/joy";
import {contract} from "@/atoms/common.ts";
import Factories from "@/utils/factories.ts";
import useLoans from "@/hooks/loans.ts";

interface ItemProps {
    obj: LoanInterface
}


const CreditLineNotDisbursedListItem:React.FC<ItemProps> = ({obj}) => {

    const {
        created_at,
        capital,
        urn,
        status,
        product_name,
    } = obj

    const {t} = useTranslation()
    const navigate = useNavigate();
    const common_hook = useCommon()
    const [user] = useAtom(profile)
    const loans = useLoans()
    const verified =  user?.kyc_general_status === "ProcessingSuccess" || user?.auth_type === "ByMPass"
    const [contract_atom] = useAtom(contract)
    const [contract_error, set_contract_error] = useState<boolean>(false)
    const [contract_enable, set_contract_enable] = useState<boolean | undefined>(undefined)
    const status_simplified = Factories.application_status_simplification(status)
    const effect_ran = useRef<boolean>(false)
    const interval = useRef<any>(null)

    useEffect(() => {
        if(obj.status === "approved_and_need_sign" && obj.is_enable_to_sign && obj.is_contract_downloadable) {
            const upd = async () => {
                let is_contract = await loans.prefetch_contract({
                    id: obj?.id,
                    uid: obj?.urn
                }, obj?.urn)

                if(!is_contract) {
                    let count = 0

                    const interval = setInterval(async () => {
                        count+=1

                        is_contract = await loans.prefetch_contract({
                            id: obj?.id,
                            uid: obj?.urn
                        }, obj?.urn)

                        set_contract_enable(is_contract)

                        if(is_contract || count > 10) {
                            clearInterval(interval)
                        }

                        if(count > 4){
                            set_contract_error(true)
                        }
                    }, 10000)
                } else {
                    set_contract_enable(is_contract)
                }
            }

            if(!effect_ran.current) {
                upd()
            }

            effect_ran.current = true
        }

        return () => {
            clearInterval(interval.current)
        }
    }, [status_simplified, user]);

    const showSignButton= obj.is_contract_downloadable &&
        (!user?.identity_card_is_expired || !user?.has_asp_kyc_conflict)

    return (
        <div
            className={clsx(
                st.credit_line_list_item,
                st.not_disbursed,
                st[status_simplified]
            )}
        >
            <div className={st.top}>
                <div className={st.top_left}>
                    <strong
                        className={st.urn}
                        onClick={(e) => {
                            e.stopPropagation();
                            common_hook.copyToClipboard(urn)
                        }}
                    >
                        {t("credit_line_nr").replace('{{name}}', t(product_name))}  {urn}
                    </strong>

                    <span>
                        {t("requested")} {common_hook.formatMomentDate(created_at)}
                    </span>
                </div>

                <div className={st.top_right}>
                    <div className={st.status}>
                        {(() => {
                            if (status === "sign_with_kyc" && user?.kyc_general_status === "ProcessingSuccess") {
                                return t("processing")
                            } else if (status === "approved" && user?.kyc_general_status === "ProcessingSuccess") {
                                return t("approved_and_payment_pending")
                            } else return  t("line_credit_status_"+ status)
                        })()}
                    </div>
                </div>
            </div>

            <div className={clsx(st.center)}>
                <div className={st.loan_sum}>
                    <span>{t("amount")}</span>
                    <strong>
                        {CommonClass.formatMoneyAmount(Number(capital))} {t("currency")}
                    </strong>
                </div>
            </div>

            <div className={st.bottom}>
                {(status === "approved_and_not_verified" || status === "approved_and_need_sign") && (
                    <>
                        {!obj.is_enable_to_sign ? (
                            <p className={st.danger}>{t('is_enabled_to_sign_error')}</p>
                        ) : showSignButton ? (
                            <Button
                                disabled={
                                    (status === "approved_and_need_sign" && verified && !contract_atom) ||
                                    (status === "approved_and_not_verified" && !user?.permissions.is_kyc_allowed) ||
                                    (status === "approved_and_need_sign" && !verified && !user?.permissions.is_kyc_allowed) ||
                                    (status === "approved_and_not_verified" && user?.kyc_general_status === "ProcessingFailedAndSentToManager") ||
                                    (status === "approved_and_need_sign" && user?.kyc_general_status === "ProcessingFailedAndSentToManager")
                                }
                                className={st.sign_contract}
                                onClick={() => {
                                    if ((status === "approved_and_need_sign" && !verified) || status === "approved_and_not_verified") {
                                        if (user?.kyc_general_status === "ProcessingSuccess") {
                                            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "info", t("data_is_processing"))
                                            return
                                        }

                                        navigate(DashboardClass.dashboard_routes.KYC)
                                    } else {
                                        navigate(DashboardClass.dashboard_routes.SignContract + "/" + obj.urn + "?is_cr_line=true")
                                    }
                                }}
                            >

                                {!verified ? t("got_to_kyc") : (
                                    <>
                                        {(!contract_atom && !contract_error) &&
                                            <CircularProgress color={"neutral"} size={"sm"}/>}
                                        {t("sign_contract")}
                                    </>
                                )}
                            </Button>
                            )
                        : null}

                        {((status === "approved_and_not_verified" && user?.kyc_general_status === "ProcessingFailedAndSentToManager") ||
                            (status === "approved_and_need_sign" && user?.kyc_general_status === "ProcessingFailedAndSentToManager")) && (
                            <span className={st.danger}>
                            {t("failed_kyc1")}
                        </span>
                        )}
                    </>
                )}

                {((status_simplified === "pending_needs_kyc" || status === "approved" || status === "approved_and_checked") && user?.kyc_general_status !== "ProcessingSuccess") && (
                    <>
                        <Button
                            disabled={!user?.permissions.is_kyc_allowed}
                            className={st.sign_contract}
                            onClick={() => {
                                navigate(DashboardClass.dashboard_routes.KYC)
                            }}
                        >
                            {t("got_to_kyc")}
                        </Button>

                        {user?.kyc_general_status === "ProcessingFailedAndSentToManager" && (
                            <span className={st.danger}>
                                            {t("failed_kyc1")}
                                        </span>
                        )}
                    </>
                )}
                {(contract_enable === false && status_simplified === "sign" && obj.is_enable_to_sign) && (
                    <span
                        className={clsx(st.sm_text, st.pending)}>{contract_error ? t("contract_err") : t("contract_is_pending")}</span>
                )}
            </div>
        </div>
    )
}

export default CreditLineNotDisbursedListItem